import React, { useEffect, useState, useCallback, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserFilters } from "../../../store/filters";
import moment from "moment";
import "./CampaignModal.scss";
import { Form, Modal, Switch, Steps, Checkbox, Radio, DatePicker } from "antd";
import { ArrowLeftOutlined, RocketOutlined } from "@ant-design/icons";
import { ReactComponent as CalendarIcon } from "../../../assets/Icons/calendarIcon.svg";
import CustomSelect from "../../../Reuseable/Select/Select";
import InputField from "../../../Reuseable/InputField/InputField";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";

const cliOptions = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

// Static options declared outside component to avoid recreation
const typeOptions = [
  { value: "progressive", label: "Progressive" },
  { value: "predictive", label: "Predictive" },
];

const CampaignModal = ({
  isOpen,
  onClose,
  onSave,
  load,
  // dispositionOptions,
  // portfolioOptions,
  // assignedAgentOptions,
  // typeOptions,
  isEdit,
  initialData,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { filter_list } = useSelector((state) => state.filters);
  const { user } = useSelector((state) => state.auth);

  const dispositionOptions = useMemo(() => {
    const data = filter_list?.find((item) => item?.name === "disposition")?.filters || [];
    return data.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  const portfolioOptions = useMemo(() => {
    const data = filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return data.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  const assignedAgentOptions = useMemo(() => {
    const data = filter_list?.find((item) => item?.name === "telecallers")?.filters || [];
    return data.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  const typeValue = Form.useWatch("type", form);
  const allowCallAgain = Form.useWatch("allow_recall_on_number", form);
  const automaticRetry = Form.useWatch("automatic_retry_enabled", form);
  const retryCountValue = Form.useWatch("retry_count", form);
  const priorityCallingValue = Form.useWatch("priority_calling", form);
  const retriesValue = Form.useWatch("retries", form);

  const getAvailableCliOptions = useCallback((currentRowIndex) => {
    const selectedClis = (retriesValue || [])
      .map((r, idx) => (idx !== currentRowIndex ? (r?.cli !== undefined && r?.cli !== null ? String(r.cli) : null) : null))
      .filter((val) => val !== null && val !== undefined && val !== "");

    return cliOptions.map((opt) => ({
      ...opt,
      disabled: selectedClis.includes(opt.value),
    }));
  }, [retriesValue]);

  // States to track toggle and disposition to manage mutual exclusivity
  const [isAllCases, setIsAllCases] = useState(false);
  const [dispositions, setDispositions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedDays, setSelectedDays] = useState([]);

  useEffect(() => {
    if (isOpen) {
      if (!user?.is_tc) {
        dispatch(fetchUserFilters());
      }
      if (isEdit && initialData) {
        let formattedAgents = [];
        if (Array.isArray(initialData.assigned_agent)) {
          formattedAgents = initialData.assigned_agent.map(agent => typeof agent === 'object' ? agent.id || agent.key : agent);
        } else if (initialData.assigned_agent) {
          formattedAgents = [initialData.assigned_agent];
        }

        const campaignType = (initialData.type || "progressive").toLowerCase();
        const isPredictive = campaignType === "predictive";

        let formattedDispositions = [];
        if (Array.isArray(initialData.disposition)) {
          formattedDispositions = initialData.disposition.map(d => typeof d === 'object' ? d.id : d);
        }

        const retryOnObject = {};
        const backendRetries = initialData.retry_on || [];
        const reverseRetryMapping = {
          "all_calls": "All Calls",
          "both_answered": "Both Answered",
          "cust_ans": "Cust. Ans",
          "agent_unanswered": "Agent Unanswered",
          "both_unanswered": "Both Unanswered",
          "rejected_call": "Rejected Call",
          "agent_failed": "Agent Failed.",
          "cust_failed": "Cust. Failed",
          "agent_busy": "Agent Busy",
          "cust_unans_agent_busy": "Cust. Unans - Agent Busy",
          "cust_ans_agent_unans": "Cust. Ans. - Agent Unans.",
          "cust_unans_agent_ans": "Cust. Unans - Agent Ans.",
          "cust_unans": "Cust. Unans.",
          "agent_ans": "Agent Ans.",
          "skipped": "Skipped",
          "cust_failed_agent_ans": "Cust. Failed - Agent Ans.",
          "cust_ans_agent_failed": "Cust. Ans. - Agent Failed",
          "cust_ans_agent_not_found": "Cust. Ans. - Agent Not Found",
          "cust_hangup": "Cust. Hangup"
        };
        backendRetries.forEach(val => {
          const key = reverseRetryMapping[val];
          if (key) {
            retryOnObject[key] = true;
          }
        });

        const retriesMapped = (initialData.retry_steps || []).map(step => ({
          gap: step.gap_minutes,
          cli: step.cli
        }));

        const weekDaysMapped = (initialData.week_days || []).map(day => day.toUpperCase());

        setIsAllCases(initialData.all_cases || false);
        setDispositions(formattedDispositions);
        setCurrentStep(0);
        setSelectedDays(weekDaysMapped);

        form.setFieldsValue({
          name: initialData.name,
          status: initialData.status || "active",
          type: initialData.type || "progressive",
          portfolio_id: typeof initialData.portfolio === 'object' ? initialData.portfolio?.id : (initialData.portfolio || initialData.portfolio_id),
          assigned_agent: isPredictive ? formattedAgents : (formattedAgents[0] || undefined),
          disposition: formattedDispositions,
          priority_calling: initialData.priority_calling,
          priority_calling_order: initialData.priority_calling_order || "ascending",
          all_cases: initialData.all_cases || false,
          allow_recall_on_number: initialData.allow_recall_on_number || false,
          retry_applicable_from: initialData.retry_applicable_from ? moment(initialData.retry_applicable_from) : null,
          automatic_retry_enabled: initialData.automatic_retry_enabled || false,
          retry_on: retryOnObject,
          retry_count: initialData.retry_count || 0,
          retries: retriesMapped,
          retry_same_agent_only: initialData.retry_same_agent_only || false,
          retry_when_all_tries_done: initialData.retry_when_all_tries_done || false,
          working_hours_start_hour: initialData.working_hours_start_hour !== undefined ? initialData.working_hours_start_hour : 0,
          working_hours_start_minute: initialData.working_hours_start_minute !== undefined ? initialData.working_hours_start_minute : 0,
          working_hours_end_hour: initialData.working_hours_end_hour !== undefined ? initialData.working_hours_end_hour : 0,
          working_hours_end_minute: initialData.working_hours_end_minute !== undefined ? initialData.working_hours_end_minute : 0,
          week_days: weekDaysMapped,
          start_date: initialData.start_date ? moment(initialData.start_date) : null,
          end_date: initialData.end_date ? moment(initialData.end_date) : null,
        });
      } else {
        form.resetFields();
        setIsAllCases(false);
        setDispositions([]);
        setCurrentStep(0);
        setSelectedDays([]);
        form.setFieldsValue({
          priority_calling: undefined,
          priority_calling_order: "ascending",
          proactive: false,
          show_contact: false,
          allow_recall_on_number: false,
          automatic_retry_enabled: false,
          retry_count: 0,
          retry_applicable_from: null,
          retry_same_agent_only: false,
          retry_when_all_tries_done: false,
          working_hours_start_hour: 0,
          working_hours_start_minute: 0,
          working_hours_end_hour: 0,
          working_hours_end_minute: 0,
          week_days: [],
        });
      }
    }
  }, [isOpen, isEdit, initialData, form, dispatch, user?.is_tc]);

  const handleDayToggle = useCallback((day) => {
    const updated = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];
    setSelectedDays(updated);
    form.setFieldsValue({ week_days: updated });
  }, [selectedDays, form]);

  const handleSave = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        if (!isEdit) {
          const hasDisposition =
            values.disposition && values.disposition.length > 0;
          const allCasesEnabled = values.all_cases;

          if (!hasDisposition && !allCasesEnabled) {
            CustomNotification.show(
              "Please select the All cases or either fill the dispostion field to proceed.",
              "error",
            );
            return;
          }
        }
        onSave?.(values);
      })
      .catch((info) => {
        console.error("Validation Failed:", info);
      });
  }, [form, onSave, isEdit]);

  const handleAllCasesChange = useCallback((checked) => {
    setIsAllCases(checked);
    if (checked) {
      form.setFieldsValue({ disposition: [] });
      setDispositions([]);
    }
  }, [form]);

  const handleDispositionChange = useCallback((value) => {
    setDispositions(value);
    if (value && value.length > 0) {
      form.setFieldsValue({ all_cases: false });
      setIsAllCases(false);
    }
  }, [form]);

  const handleTypeChange = useCallback(() => {
    form.setFieldsValue({ assigned_agent: undefined});
  }, [form]);

  const handleNext = useCallback(() => {
    if (currentStep === 0) {
      form
        .validateFields()
        .then((values) => {
          if (!isEdit) {
            const hasDisposition =
              values.disposition && values.disposition.length > 0;
            const allCasesEnabled = values.all_cases;

            if (!hasDisposition && !allCasesEnabled) {
              CustomNotification.show(
                "Please select the All cases or either fill the dispostion field to proceed.",
                "error",
              );
              return;
            }
          }
          setCurrentStep(1);
        })
        .catch((info) => {
          console.error("Validation Failed:", info);
        });
    } else if (currentStep === 1) {
      setCurrentStep(2);
    }
  }, [currentStep, form, isEdit]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // const typeOptions = [
  //   { value: "progressive", label: "Progressive" },
  //   { value: "predictive", label: "Predictive" },
  // ];

  // const portfolioOptions = [
  //   { value: 235, label: "Portfolio 235" },
  //   { value: 236, label: "Portfolio 236" },
  // ];

  // const agentOptions = [
  //   { value: 12, label: "Agent 12" },
  //   { value: 13, label: "Agent 13" },
  // ];

  const getModalTitle = () => {
    return isEdit ? "Edit Campaign" : "Add Campaign";
  };

  if (!isOpen) return null;

  return (
    <div className="campaignPageContainer animate-fade-in">
      {/* The title used to be centred with the back control absolutely
          positioned beside it; now it reads as one left-aligned bar like the
          other pages, with the channel glyph naming the section. */}
      <div className="campaignPageHeader">
        <button
          className="backLinkBtn"
          onClick={onClose}
          type="button"
          aria-label="Back to Campaigns"
        >
          <ArrowLeftOutlined />
        </button>
        <span className="campaignPageIcon">
          <RocketOutlined />
        </span>
        <div className="campaignPageTitles">
          <h1 className="campaignPageTitle">{getModalTitle()}</h1>
          <p className="campaignPageSubtitle">
            Pick the leads, set the dialling rules, then choose when it runs.
          </p>
        </div>
      </div>

      <div className="campaignPageCard">
        <Steps
          className="campaignSteps"
          current={currentStep}
          onChange={setCurrentStep}
          size="small"
          items={[
            { title: "Portfolio List" },
            { title: "Settings" },
            { title: "Timing" },
          ]}
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            allow_recall_on_number: false,
            automatic_retry_enabled: false,
            retry_same_agent_only: false,
            retry_when_all_tries_done: false,
            priority_calling: undefined,
            priority_calling_order: "ascending",
            retry_count: 0,
            working_hours_start_hour: 0,
            working_hours_start_minute: 0,
            working_hours_end_hour: 0,
            working_hours_end_minute: 0,
            week_days: [],
          }}
        >
          {/* Step 1: Portfolio List */}
          <div style={{ display: currentStep === 0 ? "block" : "none" }}>
            <div className="selectorWrapper">
              <div className="wrappedContainer">
                <Form.Item
                  name="name"
                  className="formItem"
                  label="Name"
                  rules={[{ required: true, message: "Please enter name" }]}
                >
                  <InputField placeholder="Enter campaign name" />
                </Form.Item>
              </div>
            </div>

            <div className="selectorWrapper">
              <div className="wrappedContainer">
                <Form.Item
                  name="type"
                  className="formItem"
                  label="Type"
                  rules={[{ required: true, message: "Please select type" }]}
                >
                  <CustomSelect
                    options={typeOptions}
                    placeholder="Select type"
                    onChange={handleTypeChange}
                  />
                </Form.Item>
              </div>
              <div className="wrappedContainer">
                <Form.Item
                  name="portfolio_id"
                  className="formItem"
                  label="Portfolio"
                  rules={[{ required: true, message: "Please select portfolio" }]}
                >
                  <CustomSelect
                    options={portfolioOptions || []}
                    placeholder="Select portfolio"
                  />
                </Form.Item>
              </div>
            </div>

            <div className="selectorWrapper">
              <div className="wrappedContainer">
                <Form.Item
                  name="assigned_agent"
                  className="formItem"
                  label="Assigned Agent"
                  rules={[
                    { required: true, message: "Please select assigned agents" },
                  ]}
                >
                  <CustomSelect
                    isMultiSelect={typeValue === "predictive"}
                    options={assignedAgentOptions || []}
                    placeholder="Select agents"
                  />
                </Form.Item>
              </div>
              {!isEdit && (
                <div className="wrappedContainer">
                  <Form.Item
                    name="disposition"
                    className="formItem"
                    label="Disposition"
                    required={false}
                  >
                    <CustomSelect
                      isMultiSelect={true}
                      options={dispositionOptions || []}
                      placeholder="Select dispositions"
                      onChange={handleDispositionChange}
                      disabled={isAllCases}
                    />
                  </Form.Item>
                </div>
              )}
              <div className="wrappedContainer">
                <Form.Item
                  name="priority_calling"
                  className="formItem"
                  label="Priority Calling"
                  required={false}
                >
                  <CustomSelect
                    allowClear
                    options={[
                      { value: "ltv", label: "LTV" },
                      { value: "age", label: "Age" },
                      { value: "tos", label: "TOS" },
                      { value: "pos", label: "POS" },
                    ]}
                    placeholder="Select priority calling"
                    disabled={isAllCases}
                  />
                </Form.Item>
              </div>
            </div>

            {priorityCallingValue && (
              <div className="selectorWrapper">
                <div className="wrappedContainer" style={{ flex: 1 }}>
                  <Form.Item name="priority_calling_order">
                    <Radio.Group className="priority-calling-radio-group">
                      <div className="priority-radio-option">
                        <Radio value="ascending">
                          <span className="radio-title">Ascending</span>
                        </Radio>
                        <div className="radio-description">
                          Oldest created contacts will be called first.
                        </div>
                      </div>
                      <div className="priority-radio-option">
                        <Radio value="descending">
                          <span className="radio-title">Descending</span>
                        </Radio>
                        <div className="radio-description">
                          Latest created contacts will be called first.
                        </div>
                      </div>
                    </Radio.Group>
                  </Form.Item>
                </div>
              </div>
            )}

            {!isEdit && (
              <div className="selectorWrapper">
                <div className="wrappedContainer">
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px", height: "40px" }}>
                    <span style={{
                      color: "var(--Text-Color-text-primary-black, #131927)",
                      fontFamily: "Gilroy",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}>
                      All Cases
                    </span>
                    <Form.Item name="all_cases" valuePropName="checked" noStyle>
                      <Switch
                        onChange={handleAllCasesChange}
                        disabled={dispositions && dispositions.length > 0}
                      />
                    </Form.Item>
                  </div>
                </div>
                <div className="wrappedContainer">
                  {/* Empty container for layout alignment */}
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Settings */}
          <div style={{ display: currentStep === 1 ? "block" : "none" }}>
            <div className="stepSettingsContainer">
              <div className="settingRowCheckbox flex-row">
                <span className="settingLabel">Allow to call again on a number if already called.</span>
                <Form.Item name="allow_recall_on_number" valuePropName="checked" noStyle>
                  <Checkbox className="custom-checkbox" />
                </Form.Item>
              </div>

              {allowCallAgain && (
                <div className="allowCallAgainSection">
                  <div className="settingRow flex-row">
                    <span className="subSettingLabel">Retry Applicable From</span>
                    <Form.Item name="retry_applicable_from" noStyle>
                      <DatePicker
                        placeholder="Retry From Date"
                        className="form-date-picker"
                        suffixIcon={<CalendarIcon />}
                        style={{ width: "200px" }}
                      />
                    </Form.Item>
                  </div>

                  <div className="settingRowCheckbox flex-row">
                    <span className="subSettingLabel">Automatic Retry</span>
                    <Form.Item name="automatic_retry_enabled" valuePropName="checked" noStyle>
                      <Checkbox className="custom-checkbox" />
                    </Form.Item>
                  </div>

                  {automaticRetry && (
                    <>
                      <div className="retryOnWrapper flex-col">
                        <span className="subSettingLabel">Retry On</span>
                        <div className="checkboxGrid">
                          <div className="checkboxCol">
                            {[
                              "All Calls",
                              "Both Answered",
                              "Cust. Ans",
                              "Agent Unanswered",
                              "Both Unanswered",
                              "Rejected Call",
                              "Agent Failed.",
                              "Cust. Failed",
                              "Agent Busy",
                              "Cust. Unans - Agent Busy"
                            ].map(opt => (
                              <div className="checkboxItem" key={opt}>
                                <Form.Item name={["retry_on", opt]} valuePropName="checked" noStyle>
                                  <Checkbox className="custom-checkbox">{opt}</Checkbox>
                                </Form.Item>
                              </div>
                            ))}
                          </div>
                          <div className="checkboxCol">
                            {[
                              "Cust. Ans. - Agent Unans.",
                              "Cust. Unans - Agent Ans.",
                              "Cust. Unans.",
                              "Agent Ans.",
                              "Skipped",
                              "Cust. Failed - Agent Ans.",
                              "Cust. Ans. - Agent Failed",
                              "Cust. Ans. - Agent Not Found",
                              "Cust. Hangup"
                            ].map(opt => (
                              <div className="checkboxItem" key={opt}>
                                <Form.Item name={["retry_on", opt]} valuePropName="checked" noStyle>
                                  <Checkbox className="custom-checkbox">{opt}</Checkbox>
                                </Form.Item>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="settingRow flex-row">
                        <span className="subSettingLabel">Retry Count</span>
                        <Form.Item
                          name="retry_count"
                          rules={[
                            {
                              validator: (_, value) => {
                                const val = parseInt(value, 10);
                                if (isNaN(val)) return Promise.resolve();
                                if (val < 0 || val > 10) {
                                  return Promise.reject(new Error("Max 10 retries allowed"));
                                }
                                return Promise.resolve();
                              }
                            }
                          ]}
                        >
                          <InputField type="number" min={0} max={10} placeholder="0" style={{ width: "100px" }} />
                        </Form.Item>
                      </div>

                      {Math.min(10, Math.max(0, parseInt(retryCountValue, 10) || 0)) > 0 && (
                        <div className="retryConfigContainer">
                          {Array.from({ length: Math.min(10, Math.max(0, parseInt(retryCountValue, 10) || 0)) }).map((_, index) => {
                            const retryNum = index + 1;
                            return (
                              <div className="retryRow" key={retryNum}>
                                <span className="retryLabel">Retry {retryNum}</span>
                                <div className="retryInputs">
                                  <div className="inputCol">
                                    <span className="inputHelperLabel">Gap Duration (in minutes)</span>
                                    <Form.Item name={["retries", index, "gap"]} noStyle>
                                      <InputField placeholder="Duration in minutes" type="number" />
                                    </Form.Item>
                                  </div>
                                  <div className="inputCol">
                                    <span className="inputHelperLabel">Select CLI</span>
                                    <Form.Item name={["retries", index, "cli"]} noStyle>
                                      <CustomSelect options={getAvailableCliOptions(index)} placeholder="Select CLI" />
                                    </Form.Item>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  <div className="settingRowCheckbox flex-row">
                    <span className="subSettingLabel">Only same agent can call</span>
                    <Form.Item name="retry_same_agent_only" valuePropName="checked" noStyle>
                      <Checkbox className="custom-checkbox" />
                    </Form.Item>
                  </div>

                  <div className="settingRowCheckbox flex-row">
                    <span className="subSettingLabel">Retry when all try done</span>
                    <Form.Item name="retry_when_all_tries_done" valuePropName="checked" noStyle>
                      <Checkbox className="custom-checkbox" />
                    </Form.Item>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Step 3: Timing */}
          <div style={{ display: currentStep === 2 ? "block" : "none" }}>
            <div className="stepTimingContainer">
              <div className="timingRow flex-col">
                <span className="timingLabel">Week Days</span>
                <Form.Item name="week_days" noStyle>
                  <div className="daysButtonContainer">
                    {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => {
                      const isSelected = selectedDays.includes(day);
                      return (
                        <button
                          type="button"
                          key={day}
                          className={`dayButton ${isSelected ? "selected" : ""}`}
                          onClick={() => handleDayToggle(day)}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </Form.Item>
              </div>

              <div className="timingRow flex-col">
                <span className="timingLabel">Working Hours</span>
                <div className="workingHoursInputs">
                  <Form.Item name="working_hours_start_hour" noStyle>
                    <InputField type="number" min={0} max={23} placeholder="0 Hour" style={{ width: "90px" }} />
                  </Form.Item>
                  <Form.Item name="working_hours_start_minute" noStyle>
                    <InputField type="number" min={0} max={59} placeholder="0 Minute" style={{ width: "90px" }} />
                  </Form.Item>
                  <span className="toSeparator">TO</span>
                  <Form.Item name="working_hours_end_hour" noStyle>
                    <InputField type="number" min={0} max={23} placeholder="0 Hour" style={{ width: "90px" }} />
                  </Form.Item>
                  <Form.Item name="working_hours_end_minute" noStyle>
                    <InputField type="number" min={0} max={59} placeholder="0 Minute" style={{ width: "90px" }} />
                  </Form.Item>
                </div>
              </div>

              <div className="timingRowDatePicker flex-col">
                <span className="timingLabel">Start Date</span>
                <div className="datePickerWrapper">
                  <Form.Item name="start_date" noStyle>
                    <DatePicker
                      style={{ width: "100%" }}
                      placeholder="Date"
                      className="form-date-picker"
                      suffixIcon={<CalendarIcon />}
                    />
                  </Form.Item>
                  <p className="helperDescription">
                    If selected time is less than current time then we will use current time.
                  </p>
                </div>
              </div>

              <div className="timingRowDatePicker flex-col">
                <span className="timingLabel">End Date</span>
                <div className="datePickerWrapper">
                  <Form.Item name="end_date" noStyle>
                    <DatePicker
                      style={{ width: "100%" }}
                      placeholder="Date"
                      className="form-date-picker"
                      suffixIcon={<CalendarIcon />}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {/* The step count sits with the controls so progress stays readable
              once the form is long enough to scroll the Steps out of view. */}
          <div className="actionButtons">
            <span className="stepCounter">Step {currentStep + 1} of 3</span>
            <div className="actionButtons__controls">
              {currentStep > 0 && (
                <NormalButton
                  type="default"
                  className="campaignGhostBtn"
                  width="auto"
                  controlHeight={42}
                  onClick={handleBack}
                >
                  Back
                </NormalButton>
              )}
              {currentStep < 2 ? (
                <NormalButton
                  type="primary"
                  className="campaignPrimaryBtn"
                  width="auto"
                  controlHeight={42}
                  onClick={handleNext}
                >
                  Next
                </NormalButton>
              ) : (
                <NormalButton
                  type="primary"
                  className="campaignPrimaryBtn"
                  width="auto"
                  controlHeight={42}
                  onClick={handleSave}
                  loading={load}
                >
                  {isEdit ? "Save Changes" : "Create Campaign"}
                </NormalButton>
              )}
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default memo(CampaignModal);
